/**
 * 任务系统共享配置
 * 统一客户端和管理员端的显示映射
 */

import type { BountyStatus } from "@/lib/supabase/types";

// ==================== 状态映射 ====================

/**
 * BountyStatus -> 中文标签 (通用)
 * 用于 Admin 和 Client 统一显示
 */
export const BOUNTY_STATUS_LABELS: Record<BountyStatus, string> = {
  drafting: "草稿",
  bidding: "投递期",
  in_progress: "开发期",
  reviewing: "审查期",
  completed: "已完成",
  cancelled: "已取消",
}

/**
 * Client 端我的任务状态映射
 * 基于 BountyStatus 转换为用户友好状态
 */
export const CLIENT_MY_TASK_STATUS_LABELS: Record<BountyStatus, string> = {
  drafting: "草稿",
  bidding: "投递期",
  in_progress: "开发期",
  reviewing: "审查期",
  completed: "已完成",
  cancelled: "已取消",
}

/**
 * 任务状态 CSS 样式类名
 */
export const BOUNTY_STATUS_STYLES: Record<BountyStatus, string> = {
  drafting: "statusPending",
  bidding: "statusActive",
  in_progress: "statusActive",
  reviewing: "statusActive",
  completed: "statusCompleted",
  cancelled: "statusCancelled",
}

// ==================== 分类映射 ====================

/**
 * 任务分类映射 (通用)
 */
export const BOUNTY_CATEGORIES: Record<string, string> = {
  design: "设计",
  development: "开发",
  brand: "品牌",
  content: "内容",
  analysis: "分析",
  video: "视频",
  other: "其他",
}

/**
 * Admin 端分类列表
 */
export const ADMIN_BOUNTY_CATEGORIES = ["全部", ...Object.values(BOUNTY_CATEGORIES)]

/**
 * Client 端大厅分类列表
 */
export const CLIENT_BOUNTY_CATEGORIES = ["全部", "Agent配置", "自动化脚本", "流程梳理", "报告生成", "数据处理"]

/**
 * 分类反向映射 (中文 -> 英文)
 */
export const BOUNTY_CATEGORY_REVERSE: Record<string, string> = {
  设计: "design",
  开发: "development",
  品牌: "brand",
  内容: "content",
  分析: "analysis",
  视频: "video",
  其他: "other",
  // Client 特有分类
  "Agent配置": "agent",
  "自动化脚本": "automation",
  "流程梳理": "flow",
  "报告生成": "report",
  "数据处理": "data",
}

// ==================== 金额格式化 ====================

/**
 * BBP 转人民币汇率 (1 BBP = 0.01 RMB)
 * 数据库存储的是 BBP 单位
 */
export const BBP_TO_RMB_RATE = 0.01

/**
 * 格式化 BBP 金额为展示字符串
 * @param bbp - 数据库中存储的 BBP 数值
 * @param showCurrency - 是否显示货币符号
 */
export function formatBBPAmount(bbp: number, showCurrency = true): string {
  const rmbValue = bbp * BBP_TO_RMB_RATE
  if (showCurrency) {
    return `¥${rmbValue.toLocaleString("zh-CN")}`
  }
  return rmbValue.toLocaleString("zh-CN")
}

/**
 * 格式化 BBP 为 BBP 展示字符串
 * @param bbp - BBP 数值
 */
export function formatBBPDisplay(bbp: number): string {
  return `${bbp.toLocaleString()} BBP`
}

// ==================== 等级计算 ====================

/**
 * 根据金额推算任务等级
 * @param rewardBbp - 奖励金额 (BBP)
 */
export function deriveLevel(rewardBbp: number): string {
  const rmb = rewardBbp * BBP_TO_RMB_RATE
  if (rmb < 10) return "L0+"
  if (rmb < 30) return "L1+"
  if (rmb < 50) return "L2+"
  if (rmb < 100) return "L3+"
  if (rmb < 200) return "L4+"
  return "L5+"
}

// ==================== 日期格式化 ====================

/**
 * 格式化日期为 YYYY-MM-DD
 */
export function formatDeadline(dateStr: string | null): string {
  if (!dateStr) return "无截止日期"
  return dateStr.slice(0, 10)
}

/**
 * 格式化日期为中文友好格式
 */
export function formatDeadlineCN(dateStr: string | null): string {
  if (!dateStr) return "无截止日期"
  return new Date(dateStr).toLocaleDateString("zh-CN")
}

// ==================== 类型导出 ====================

export type { BountyStatus } from "@/lib/supabase/types"
